import React, { useMemo } from 'react';
import { TablePagination } from '@mui/material';
import type { TableDescriptor } from '@finance/contracts';
import type { TableState } from './table-state.ts';
import { getAllowedTablePageSizes } from './table-state.ts';
import { useAppI18n } from '../../../i18n/use-app-i18n';

interface TableFooterProps {
  descriptor: TableDescriptor;
  state: TableState;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onGoToFirstPage?: () => void;
  onGoToLastPage?: () => void;
}

export function TableFooter({
  descriptor,
  state,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: TableFooterProps) {
  const { text } = useAppI18n();
  const pageSizeOptions = useMemo(
    () => getAllowedTablePageSizes(descriptor),
    [descriptor],
  );
  const page = state.pagination.page;
  const pageSize = state.pagination.pageSize;
  const currentPage = page + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <TablePagination
      component="div"
      count={totalCount}
      page={page}
      onPageChange={(_event, newPage) => onPageChange(newPage)}
      rowsPerPage={pageSize}
      rowsPerPageOptions={pageSizeOptions}
      onRowsPerPageChange={event => onPageSizeChange(Number(event.target.value))}
      slotProps={{
        select: {
          native: true,
          'aria-label': text('table.rowsPerPage'),
        },
      }}
      labelRowsPerPage={text('table.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        `${text('table.pageInfo', { start: from, end: to, total: count })} ${text(
          'table.pageOf',
          { current: currentPage, total: totalPages },
        )}`
      }
      showFirstButton
      showLastButton
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minWidth: 0,
        '& .MuiTablePagination-toolbar': {
          minHeight: 52,
          px: { xs: 1, sm: 1.5 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          rowGap: 0.5,
        },
        '& .MuiTablePagination-spacer': {
          flex: { xs: '0 0 0', sm: '1 1 100%' },
        },
        '& .MuiTablePagination-displayedRows': {
          whiteSpace: 'nowrap',
        },
      }}
    />
  );
}
